import {ChangeDetectionStrategy, Component, computed, effect, inject, input, output, Signal} from '@angular/core';
import {CardElementComponent} from "../../../../../card-wrap/card-element/card-element/card-element.component";
import {CardWrapComponent} from "../../../../../card-wrap/card-wrap/card-wrap.component";
import {SelectComponent, SelectOption} from "../../../../../form-elements/select/select/select.component";
import {ToggleComponent} from "../../../../../toggle/toggle/toggle.component";
import {LamellaSet} from "../../../lamel/lamella-set.class";
import {LamelareaStore} from "../../../lamelarea.store";
import {ColorSelectorComponent} from "../../../../../modals/color-selector/color-selector/color-selector.component";
import {ColorInfo} from "../../../../../../core/util/color-converter";
import {ProductSelectorComponent} from "../../../../../modals/product-selector/product-selector.component";
import {LamellaListingItem} from "../../../../../types/lamella-listing-item.type";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {ImageDialogComponent} from "../../../../../image-dialog/image-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {availableProductsStore} from "../../../../../store/available-products.store";
import {translate} from "../../../../../translate/translate";
import {CustomTranslateService} from "../../../../../../core/service/custom-translate.service";


@Component({
  selector: 'app-lamella-set',
  imports: [
    CardElementComponent,
    CardWrapComponent,
    SelectComponent,
    ToggleComponent,
    ColorSelectorComponent,
    ProductSelectorComponent,
    TranslatePipe
  ],
  templateUrl: './lamella-set.component.html',
  styleUrl: './lamella-set.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,

})
export class LamellaSetComponent {
  readonly lamelareaStore = inject(LamelareaStore);
  readonly availableProducts =  inject(availableProductsStore);
  readonly dialog = inject(MatDialog);
  private readonly translate: CustomTranslateService = inject(CustomTranslateService);
  readonly lamellaSet = input.required<LamellaSet>();

  readonly lamellaProduct = computed(()=> this.availableProducts.availableLamellas().find(p => p.productCode === this.lamellaSet().productCode));
  readonly lamellaRepeatDisabled = input.required<boolean>();

  colorChanged = output<string>();

  readonly lamellaSpaceBetweenOptions = computed(() => {

    if(!this.lamellaProduct()){ return [] }

    // Luetaan ensin lamellaSet-input signalin nykyinen arvo.
    // Tämä on LamellaSet-olio, joka ei ole signaali.
    const lamellaSetInstance = this.lamellaSet();

    const minValue = this.lamellaProduct()?.minSpace || 26.5; // Jos minSpace ei ole määritelty, käytetään oletusarvoa 26.5
    const maxValue = this.lamellaProduct()?.maxSpace || 200; // Jos maxSpace ei ole määritelty, käytetään oletusarvoa 200
    const step = 26.5; // Saatetaan joskus korvata kamman tiedoilla, nyt tämä hyvä näin.

    // Lasketaan montako askelta mahtuu mukaan (sisältäen aloitusarvon)
    const numberOfSteps = (maxValue - minValue) / step;
    const length = Math.floor(numberOfSteps) + 1;

    // Luodaan SelectOption-taulukko Array.fromilla
    return Array.from({ length: length }, (_, i) => {
      const value = minValue + i * step;
      let fixedLabel = value+this.lamellaProduct().spaceTaken;
      return {
        value,
        label: `${fixedLabel} mm` // Label kuten rikkinäisessä funktiossa
      };
    }).filter(option => option !== null) as SelectOption[]; // Poistetaan mahdolliset nullit ja varmistetaan tyyppi
  });


  lamellaKey: Signal<string> = translate('PRODUCT_CARD.LAMELLA_SET.LAMELLA') as Signal<string>;
  measureImageKey: Signal<string> = translate('PRODUCT_CARD.LAMELLA_SET.MEASURE_IMAGE') as Signal<string>;

  private readonly numericRepeats = Array.from({ length: 9 }, (_, i) => i + 2);
  // Yhdistetään both repeatValues ja repeatOptions yhdeksi computediksi
  repeatOptions: Signal<SelectOption[]> = computed(() => {
    const disabled    = this.lamellaRepeatDisabled();
    const continuous  = this.translate.instant('global.continuous');

    // jos disabled, pelkkä numerolistaus, muuten lisätään alkuun Infinity
    const values = !disabled
        ? this.numericRepeats
        : [Infinity, ...this.numericRepeats];

    return values.map(value => ({
      value,
      label: value === Infinity
          ? continuous
          : String(value)
    }));
  });




  // Jatkuva, 2, 3, 4, 5, 6, 7, 8, 9, 10
  //repeatOptions = this.lamellaRepeatDisabled.map(i => ({value: i, label: i === Infinity ? this.translate.instant('global.continuous') : `${i}`}));

  materialChanged(event: string | boolean): void {
    if (event !== "Powder Coated" && event !== "Anodized") {
      console.error("Invalid material type:", event);
      return;
    }
    this.lamelareaStore.updateLamelSet(this.lamellaSet().id, {material: event})
  }

  onColorSelected($event: ColorInfo, selectRef: SelectComponent) {
    this.lamelareaStore.updateLamelSet(this.lamellaSet().id, {color: $event})
    selectRef.close();
  }

  onProductSelected($event: LamellaListingItem, selectRef: SelectComponent) {

    const changes : Partial<LamellaSet>  = {
      lamellaId: $event.id,
      name : $event.productName,
      productCode: $event.productCode,
      object3dUrl: $event.model3dUrl,
      spaceAfter: $event.minSpaceBottom,
      spaceBefore: $event.minSpaceTop,
      lamellaSpace: $event.spaceTaken,
      depth: $event.depth,
      height: $event.height
    }

    if($event.minSpace > this.lamellaSet().lamellaSpaceBetween  ) {
      changes.lamellaSpaceBetween = $event.minSpace;
    }


    this.lamelareaStore.updateLamelSet(this.lamellaSet().id, changes)
  }

  onLamellaRepeatChanged(event: any) {

    if(event==null) return; // Tyyppiä vaidettaessa event käy null arvossa. Korjaa sen.

    const currentLamellaSet = this.lamellaSet();
    if(currentLamellaSet.lamellaRepeat === event.value) return; // Ei muutosta, ei päivitystä

    let value;

    if(event.value == "Jatkuva") { //TODO: Lokalisointi
        value = Infinity
    }
    else {
        value = parseInt(event.value)
    }

    this.lamelareaStore.updateLamelSet(this.lamellaSet().id, {lamellaRepeat: value})
  }

  onLamellaSpaceBetweenChanged(event: any) {
    const currentLamellaSet = this.lamellaSet();
    if (currentLamellaSet.lamellaSpaceBetween !== event.value) {
      this.lamelareaStore.updateLamelSet(currentLamellaSet.id, { lamellaSpaceBetween: event.value });
    }
  }

  openMeasureImage( ) {

    this.lamellaProduct() && this.dialog.open(ImageDialogComponent, {

      data: {
        imageFile: `/api/storage/view/${this.lamellaProduct().measureImage}`,
        title: computed(()=>  `${ this.lamellaKey() } ` + `${ this.lamellaProduct().productName } ` + `-${ this.measureImageKey() }`)
      }

    })

  }

  fixedRepeat(lamellaRepeat: number) {
    if(lamellaRepeat == Infinity) {
      return this.translate.instant('global.continuous');
    }
    return lamellaRepeat
  }
}
